import * as React from 'react'
import FlowiumClient, { ResponseType, User, Workspace, Project } from '@flowium/client'
import { useNavigate } from 'react-router'
import LoadingPage from '../components/LoadingPage'
import { getFrontendRelease } from '../telemetry'
import { getErrorMessage } from '../utils'

interface FlowiumContextType {
    client: FlowiumClient
    isLoading: boolean
    isInitializing: boolean
    error: string | null
    login: ({ email, password, googleTokenId }: { email?: string, password?: string, googleTokenId?: string }) => Promise<void>
    logout: () => void
    isAuthenticated: boolean
    user: User | null
    workspaces: Workspace[]
    projects: Project[]
    currentWorkspace: Workspace | undefined
    currentProject: Project | undefined
    fetchUserData: () => Promise<void>
    showLoading: () => void
    hideLoading: () => void
}

const FlowiumContext = React.createContext<FlowiumContextType | null>(null)

interface FlowiumProviderProps {
    baseUrl: string
    fallbackBaseUrl?: string
    children: React.ReactNode
}

export const FlowiumProvider: React.FC<FlowiumProviderProps> = ({ baseUrl, fallbackBaseUrl, children }) => {
    const [baseLoading, setBaseLoading] = React.useState(true)
    const [overlayCount, setOverlayCount] = React.useState(0)
    const [error, setError] = React.useState<string | null>(null)
    const [isAuthenticated, setIsAuthenticated] = React.useState(false)
    const [user, setUser] = React.useState<User | null>(null)
    const [workspaces, setWorkspaces] = React.useState<Workspace[]>([])
    const [projects, setProjects] = React.useState<Project[]>([])
    const [client] = React.useState(() => {
        const flowiumClient = new FlowiumClient(baseUrl, undefined, fallbackBaseUrl)
        flowiumClient.setClientRelease(getFrontendRelease())
        return flowiumClient
    })
    const navigate = useNavigate()
    const isLoading = baseLoading || overlayCount > 0
    const isInitializing = baseLoading

    const showLoading = React.useCallback(() => {
        "Show the global loading overlay."
        setOverlayCount(prev => prev + 1)
    }, [])

    const hideLoading = React.useCallback(() => {
        "Hide the global loading overlay."
        setOverlayCount(prev => (prev > 0 ? prev - 1 : 0))
    }, [])

    const currentWorkspace = React.useMemo(() =>
        workspaces.find(w => w.id === user?.workspace),
        [workspaces, user?.workspace]
    )

    const currentProject = React.useMemo(() =>
        projects.find(p => p.id === user?.project),
        [projects, user?.project]
    )

    const fetchUserData = React.useCallback(async () => {
        try {
            const userData = await client.getUser()
            if (userData === ResponseType.NOT_FOUND || userData === ResponseType.NOT_VERIFIED) {
                setUser(null)
                setWorkspaces([])
                setProjects([])
                return
            }

            if (typeof userData === 'object' && userData !== null) {
                const resolvedUser = userData as User
                const [paymentsResult, workspacesResult, projectsResult] = await Promise.allSettled([
                    client.getUserPayments(),
                    client.getWorkspaces(),
                    client.getProjects(resolvedUser.workspace),
                ])

                const payments = paymentsResult.status === 'fulfilled' ? paymentsResult.value : []
                const workspacesData = workspacesResult.status === 'fulfilled' ? workspacesResult.value : []
                const projectsData = projectsResult.status === 'fulfilled' ? projectsResult.value : []

                setUser({ ...resolvedUser, payments })
                setWorkspaces(workspacesData as Workspace[])
                setProjects(projectsData as Project[])
            }
        } catch (err) {
            // silently handled — user sees unauthenticated state
        }
    }, [client])

    // check auth state on mount
    React.useEffect(() => {
        const checkAuth = async () => {
            try {
                const token = localStorage.getItem('auth_token')
                if (token) {
                    client.setAuth(token, true)
                    await client.checkLogin()
                    setIsAuthenticated(true)
                    await fetchUserData()
                    // only redirect to browser if we're on the root path
                    if (window.location.pathname === '/') {
                        navigate('/browser')
                    }
                }
            } catch (err) {
                localStorage.removeItem('auth_token')
            } finally {
                setBaseLoading(false)
            }
        }
        checkAuth()
    }, [client, navigate, fetchUserData])

    const login = React.useCallback(async ({ email, password, googleTokenId }: { email?: string, password?: string, googleTokenId?: string }) => {
        if (googleTokenId) {
            try {
                const token = await client.googleLogin(googleTokenId)
                localStorage.setItem('auth_token', token)
                setIsAuthenticated(true)
                await fetchUserData()
                navigate('/browser')
            } catch (err) {
                setError(getErrorMessage(err) || 'Google login failed')
                throw err
            }
            return
        }

        setBaseLoading(true)
        setError(null)
        try {
            const token = await client.login(email!, password!)
            localStorage.setItem('auth_token', token ?? '')
            setIsAuthenticated(true)
            await fetchUserData()
            navigate('/browser')
        } catch (err) {
            setError(getErrorMessage(err) || 'Login failed')
            throw err
        } finally {
            setBaseLoading(false)
        }
    }, [client, navigate, fetchUserData])

    const logout = React.useCallback(() => {
        localStorage.removeItem('auth_token')
        client.setAuth('', true)
        setIsAuthenticated(false)
        setUser(null)
        setWorkspaces([])
        setProjects([])
    }, [client])

    const contextValue = React.useMemo(() => ({
        client,
        isLoading,
        isInitializing,
        error,
        login,
        logout,
        isAuthenticated,
        user,
        workspaces,
        projects,
        currentWorkspace,
        currentProject,
        fetchUserData,
        showLoading,
        hideLoading
    }), [client, isLoading, isInitializing, error, login, logout, isAuthenticated,
         user, workspaces, projects, currentWorkspace, currentProject,
         fetchUserData, showLoading, hideLoading])

    return (
        <FlowiumContext.Provider value={contextValue}>
            {children}
            {isLoading && <LoadingPage />}
        </FlowiumContext.Provider>
    )
}

export const useFlowium = () => {
    const context = React.useContext(FlowiumContext)
    if (!context) {
        throw new Error('useFlowium must be used within a FlowiumProvider')
    }
    return context
}
